import { Injectable, NgZone } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { events } from 'aws-amplify/data';
import { environment } from '../../../environments/environment';
import { IdService } from '../id/id.service';
import { RegionService } from '../region/region.service';

export interface AppsyncResponse {
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppsyncService {
  private channel: any;
  private subscription: any;

  constructor(
    private id: IdService,
    private regionService: RegionService,
    private ngZone: NgZone,
  ) {}

  private async configureAmplify(): Promise<void> {
    const region = (this.regionService.getRegion() ?? 'au').toLowerCase();
    let config = environment.APPSYNC[region as keyof typeof environment.APPSYNC];
    if (!config) {
      config = environment.APPSYNC['au'];
    }
    if (!config?.ENDPOINT || !config?.REGION) {
      throw new Error(`AppSync configuration is missing for region: ${region}`);
    }

    Amplify.configure({
      API: {
        Events: {
          endpoint: config.ENDPOINT,
          region: config.REGION,
          defaultAuthMode: 'lambda',
        },
      },
    });
  }

  /**
   * Subscribe to a channel and automatically close when status is "published"
   * @param channelNamespace The namespace for the channel
   * @param hashID The hash ID for the channel
   * @param onPublished Optional callback function called when status is "published" before closing
   * @returns Promise that resolves when connection is established
   */
  async subscribeToChannel(
    channelNamespace: string,
    hashID: string,
    onPublished?: (event: AppsyncResponse) => void,
  ): Promise<void> {
    this.cleanupExistingConnection();
    await this.configureAmplify();
    const token = this.id.getAuthToken();

    const channelPath = `${channelNamespace}/${hashID}`;
    const connectOptions: any = {
      authToken: token,
    };
    this.channel = await this.openChannel(channelPath, connectOptions);
    this.subscription = this.channel.subscribe({
      next: (event: any) => this.handleEvent(event, onPublished),
      error: () => {},
      complete: () => {},
    });
  }

  /**
   * Close the connection and unsubscribe
   * Safe to call multiple times - handles null-safe cleanup
   */
  closeConnection(): void {
    this.unsubscribe();
    this.closeChannel();
  }

  // Own method so specs can stub the socket. The Amplify export is not writable in the test bundle.
  private openChannel(channelPath: string, connectOptions: { authToken: string }) {
    return events.connect(channelPath, connectOptions);
  }

  private cleanupExistingConnection(): void {
    if (this.subscription || this.channel) {
      this.closeConnection();
    }
  }

  private handleEvent(event: any, onPublished?: (event: AppsyncResponse) => void): void {
    const eventData = event?.event as AppsyncResponse | undefined;

    if (eventData?.status === 'published') {
      this.ngZone.run(() => {
        onPublished?.(eventData);
      });
      this.closeConnection();
    }
  }

  private unsubscribe(): void {
    if (!this.subscription) {
      return;
    }

    try {
      this.subscription.unsubscribe();
    } catch (error) {
      // Silently handle unsubscribe errors
    } finally {
      this.subscription = null;
    }
  }

  private closeChannel(): void {
    if (!this.channel) {
      return;
    }

    try {
      this.channel.close();
    } catch (error) {
      // Silently handle close errors
    } finally {
      this.channel = null;
    }
  }
}
